import React from 'react';
import PropTypes from 'prop-types';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import useArtifactContent from '../../hooks/useArtifactContent';
import css from './ArtifactLoader.css';

/**
 * Wrapper that handles lazy-loading artifact content from the backend.
 *
 * - Streaming path: `artifact.data` is present → renders children immediately.
 * - Reload path: `artifact.content_url` is present → fetches content, shows
 *   a spinner while loading, and passes the resolved data to children.
 *
 * Usage:
 *   <ArtifactLoader artifact={artifact}>
 *       {data => <GridArtifactInner data={data} ... />}
 *   </ArtifactLoader>
 */
const ArtifactLoader = ({ artifact, children }) => {
    const hasInlineData = artifact.data != null;
    const contentUrl = hasInlineData ? null : artifact.content_url;
    const { content: fetchedContent, isLoading, error } = useArtifactContent(contentUrl);
    const resolvedData = hasInlineData ? artifact.data : fetchedContent;

    if (isLoading) {
        return (
            <div className={css.artifactLoaderState}>
                <Icon name="loader" size={IconSizes.LARGE} className={css.spinner} />
                <div>Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={css.artifactLoaderState}>
                <Icon name="alert-triangle" size={IconSizes.LARGE} variant={IconVariants.ERROR} />
                <div>Failed to load data</div>
            </div>
        );
    }

    return children(resolvedData);
};

ArtifactLoader.propTypes = {
    artifact: PropTypes.shape({
        data: PropTypes.any,
        content_url: PropTypes.string,
    }).isRequired,
    children: PropTypes.func.isRequired,
};

export default ArtifactLoader;
